import React, { useId } from 'react';
import { useSelector } from 'react-redux';
import { notification, Switch } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DateTime } from 'luxon';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getApiErrorMessage } from 'shared/utils';
import { useCreateQualityMetric, useUpdateQualityMetric } from 'shared/hooks/quality-metric.hooks';
import {
  DatePicker,
  RequiredIndication,
  TextAreaInput,
  TextInput,
} from 'src/components/design-system';
import { Modal } from 'src/components/design-system/modal';
import styles from '../quality-metrics-management.module.scss';

const TITLE_MIN = 2;
const TITLE_MAX = 40;
const PRIORITY_MAX = 1000;

/** Free-text fields the API treats as optional. */
const OPTIONAL_TEXT_FIELDS = ['description', 'numerator', 'denominator', 'compliance'];

const emptyValues = {
  title: '',
  description: '',
  numerator: '',
  denominator: '',
  compliance: '',
  // Held as a 0–100 percentage for editing; the API wants 0–1.
  goalPercentage: '',
  minimumRecords: '',
  startDate: '',
  invert: false,
  priority: '0',
  isHidden: false,
};

const toFormValues = metric =>
  metric
    ? {
        title: metric.title ?? '',
        description: metric.description ?? '',
        numerator: metric.numerator ?? '',
        denominator: metric.denominator ?? '',
        compliance: metric.compliance ?? '',
        goalPercentage: String(Math.round(metric.groupGoal * 1000) / 10),
        minimumRecords: String(metric.minimumRecords ?? 0),
        startDate: metric.startDate ?? '',
        invert: !!metric.invert,
        priority: String(metric.priority ?? 0),
        isHidden: !!metric.isHidden,
      }
    : emptyValues;

/**
 * Create / edit a quality metric. Editing when `metric` is set, creating
 * otherwise. Resources are not edited here — they save immediately and have
 * their own modal, so mixing them into this draft-then-submit form would give
 * one dialog two different save semantics.
 */
export function MetricFormModal({ open, metric, onClose }) {
  const lang = useSelector(getLang('QUALITY_METRICS_MANAGEMENT'));
  const [api, contextHolder] = notification.useNotification();
  const createMetric = useCreateQualityMetric();
  const updateMetric = useUpdateQualityMetric();
  const isEdit = !!metric;
  const switchId = useId();

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .min(TITLE_MIN, lang.VALIDATION_TITLE_LENGTH)
      .max(TITLE_MAX, lang.VALIDATION_TITLE_LENGTH)
      .required(lang.VALIDATION_REQUIRED),
    description: Yup.string().trim(),
    numerator: Yup.string().trim(),
    denominator: Yup.string().trim(),
    compliance: Yup.string().trim(),
    invert: Yup.boolean(),
    isHidden: Yup.boolean(),
    goalPercentage: Yup.number()
      .typeError(lang.VALIDATION_GOAL_RANGE)
      .min(0, lang.VALIDATION_GOAL_RANGE)
      .max(100, lang.VALIDATION_GOAL_RANGE)
      .required(lang.VALIDATION_REQUIRED),
    minimumRecords: Yup.number()
      .typeError(lang.VALIDATION_MIN_RECORDS)
      .integer(lang.VALIDATION_MIN_RECORDS)
      .min(0, lang.VALIDATION_MIN_RECORDS)
      .required(lang.VALIDATION_REQUIRED),
    startDate: Yup.string().required(lang.VALIDATION_REQUIRED),
    priority: Yup.number()
      .typeError(lang.VALIDATION_PRIORITY_RANGE)
      .integer(lang.VALIDATION_PRIORITY_RANGE)
      .min(0, lang.VALIDATION_PRIORITY_RANGE)
      .max(PRIORITY_MAX, lang.VALIDATION_PRIORITY_RANGE)
      .required(lang.VALIDATION_REQUIRED),
  });

  const { handleSubmit, setFieldValue, resetForm, values, errors, touched, handleBlur } = useFormik(
    {
      enableReinitialize: true,
      validationSchema,
      initialValues: toFormValues(metric),
      onSubmit: async vals => {
        // Formik validates against the schema but does not apply its casts, so
        // cast here for the trimmed strings and real numbers.
        const { goalPercentage, ...rest } = validationSchema.cast(vals, { stripUnknown: true });
        const payload = { ...rest, groupGoal: goalPercentage / 100 };

        // Both schemas declare these as bare `Joi.string()`, which rejects "",
        // so a blank is omitted rather than sent. The cost is that clearing a
        // field from the form is not possible: PUT writes only the keys it
        // receives, so the old text stays. Clearing needs `.allow('')` on the
        // backend update schema first, and lands with it.
        for (const field of OPTIONAL_TEXT_FIELDS) {
          if (!payload[field]) delete payload[field];
        }

        try {
          if (isEdit) {
            await updateMetric.mutateAsync({ id: metric.id, payload });
          } else {
            await createMetric.mutateAsync(payload);
          }
          api.success({
            message: isEdit ? lang.METRIC_UPDATED : lang.METRIC_CREATED,
            placement: 'bottomRight',
          });
          handleClose();
        } catch (error) {
          api.error({
            message: getApiErrorMessage(error) || lang.ERROR_GENERIC,
            placement: 'bottomRight',
          });
        }
      },
    },
  );

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const errorFor = field => (touched[field] ? errors[field] : undefined);
  const loading = createMetric.isPending || updateMetric.isPending;

  return (
    <>
      {contextHolder}
      <Modal
        title={isEdit ? lang.EDIT_METRIC : lang.NEW_METRIC}
        open={open}
        onCancel={handleClose}
        onSubmit={handleSubmit}
        cancelText={lang.CANCEL}
        submitText={isEdit ? lang.SAVE : lang.CREATE}
        loading={loading}
        footerAlign="end"
        width={640}
        className={styles.formModal}>
        <TextInput
          label={<RequiredIndication label={lang.FIELD_TITLE} />}
          name="title"
          value={values.title}
          onChange={e => setFieldValue('title', e.target.value)}
          onBlur={handleBlur}
          error={errorFor('title')}
          maxLength={TITLE_MAX}
          size="middle"
        />

        <TextAreaInput
          label={lang.FIELD_DESCRIPTION}
          name="description"
          value={values.description}
          onChange={e => setFieldValue('description', e.target.value)}
          rows={2}
          size="middle"
        />

        <TextAreaInput
          label={lang.FIELD_NUMERATOR}
          name="numerator"
          value={values.numerator}
          onChange={e => setFieldValue('numerator', e.target.value)}
          rows={2}
          size="middle"
        />

        <TextAreaInput
          label={lang.FIELD_DENOMINATOR}
          name="denominator"
          value={values.denominator}
          onChange={e => setFieldValue('denominator', e.target.value)}
          rows={2}
          size="middle"
        />

        <TextAreaInput
          label={lang.FIELD_COMPLIANCE}
          name="compliance"
          value={values.compliance}
          onChange={e => setFieldValue('compliance', e.target.value)}
          rows={2}
          size="middle"
        />

        <div className={styles.fieldRow}>
          <TextInput
            label={<RequiredIndication label={lang.FIELD_GOAL} />}
            name="goalPercentage"
            type="number"
            min={0}
            max={100}
            suffix="%"
            value={values.goalPercentage}
            onChange={e => setFieldValue('goalPercentage', e.target.value)}
            onBlur={handleBlur}
            error={errorFor('goalPercentage')}
            size="middle"
          />
          <TextInput
            label={<RequiredIndication label={lang.FIELD_MIN_RECORDS} />}
            name="minimumRecords"
            type="number"
            min={0}
            value={values.minimumRecords}
            onChange={e => setFieldValue('minimumRecords', e.target.value)}
            onBlur={handleBlur}
            error={errorFor('minimumRecords')}
            size="middle"
          />
          <TextInput
            label={lang.FIELD_PRIORITY}
            name="priority"
            type="number"
            min={0}
            max={PRIORITY_MAX}
            value={values.priority}
            onChange={e => setFieldValue('priority', e.target.value)}
            onBlur={handleBlur}
            error={errorFor('priority')}
            size="middle"
          />
        </div>

        <DatePicker
          label={<RequiredIndication label={lang.FIELD_START_DATE} />}
          name="startDate"
          value={values.startDate}
          onChange={next => setFieldValue('startDate', next ? next.toUTC().toISO() : '')}
          error={errorFor('startDate')}
          clearLabel={lang.FIELD_START_DATE}
          disabledDate={current => current && current > DateTime.now()}
        />
        <p className={styles.fieldHint}>{lang.WARN_START_DATE}</p>

        <div className={styles.switchWrapper}>
          <Switch
            id={`${switchId}-invert`}
            style={{ width: '2rem' }}
            checked={values.invert}
            onChange={checked => setFieldValue('invert', checked)}
          />
          <label htmlFor={`${switchId}-invert`}>{lang.FIELD_INVERT}</label>
        </div>

        <div className={styles.switchWrapper}>
          <Switch
            id={`${switchId}-isHidden`}
            style={{ width: '2rem' }}
            checked={values.isHidden}
            onChange={checked => setFieldValue('isHidden', checked)}
          />
          <label htmlFor={`${switchId}-isHidden`}>{lang.FIELD_HIDDEN}</label>
        </div>

        {isEdit && <p className={styles.fieldHint}>{lang.WARN_PROPAGATES}</p>}
      </Modal>
    </>
  );
}
